import React, { forwardRef } from "react";
import { defaultLayoutIcons } from '@vidstack/react/player/layouts/default';
import { CaptionButton, FullscreenButton, isTrackCaptionKind, MuteButton, PIPButton, PlayButton, SeekButton, Tooltip, useMediaState, type TooltipPlacement, AirPlayButton, GoogleCastButton } from '@vidstack/react';
import { AirPlayIcon, PictureInPictureExitIcon, PictureInPictureIcon, SeekBackward10Icon, SeekForward10Icon } from '@vidstack/react/icons';
import { BraivCaptionOffIcon, BraivCaptionOnIcon, BraivEnterFullScreenIcon, BraivExitFullScreenIcon, BraivPauseIcon, BraivPlayIcon, BraivVolumeHighIcon, BraivVolumeLowIcon, BraivVolumeMuteIcon, PlayerIcons } from "@/components/player/layouts/shared/playerIcons";
import { DefaultTooltip } from "@vidstack/react/player/layouts/default";
export interface MediaButtonProps {
  tooltipPlacement: TooltipPlacement;
}
export function Play({
  tooltipPlacement
}: MediaButtonProps) {
  const isPaused = useMediaState('paused');
  return <Tooltip.Root data-sentry-element="unknown" data-sentry-component="Play" data-sentry-source-file="buttons.tsx">
            <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                <PlayButton className="vds-play-button vds-button" data-sentry-element="PlayButton" data-sentry-source-file="buttons.tsx">
                    {isPaused ? <BraivPlayIcon className={"vds-icon"} /> : <BraivPauseIcon />}
                </PlayButton>
            </Tooltip.Trigger>
            <Tooltip.Content className="vds-tooltip-content" placement={tooltipPlacement} data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                {isPaused ? 'Play' : 'Pause'}
            </Tooltip.Content>
        </Tooltip.Root>;
}
const Mute = forwardRef<HTMLButtonElement, MediaButtonProps>(({
  tooltipPlacement
}, forwardRef) => {
  const isMuted = useMediaState('muted'),
    volume = useMediaState('volume');
  return <Tooltip.Root>
                <Tooltip.Trigger asChild>
                    <MuteButton className="vds-mute-button vds-button" aria-label={'Mute'} ref={forwardRef}>
                        {isMuted || volume == 0 ? <BraivVolumeMuteIcon className="vds-icon" /> : volume < 0.5 ? <BraivVolumeLowIcon className="vds-icon" /> : <BraivVolumeHighIcon className="vds-icon" />}
                    </MuteButton>
                </Tooltip.Trigger>
                <Tooltip.Content className="vds-tooltip-content" placement={tooltipPlacement}>
                    {isMuted ? 'Unmute' : 'Mute'}
                </Tooltip.Content>
            </Tooltip.Root>;
});
Mute.displayName = 'Mute';
export { Mute };

/**
 * Deprecated: Use `CaptionButton` instead.
 * @param tooltipPlacement
 * @constructor
 */
export function Caption({
  tooltipPlacement
}: MediaButtonProps) {
  const track = useMediaState('textTrack'),
    isOn = track && isTrackCaptionKind(track);
  return <Tooltip.Root data-sentry-element="unknown" data-sentry-component="Caption" data-sentry-source-file="buttons.tsx">
            <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                <CaptionButton className="vds-button" data-sentry-element="CaptionButton" data-sentry-source-file="buttons.tsx">
                    {isOn ? <BraivCaptionOnIcon /> : <BraivCaptionOffIcon />}
                </CaptionButton>
            </Tooltip.Trigger>
            <Tooltip.Content className="vds-tooltip-content" placement={tooltipPlacement} data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                {isOn ? 'Closed-Captions Off' : 'Closed-Captions On'}
            </Tooltip.Content>
        </Tooltip.Root>;
}
export function PIP({
  tooltipPlacement
}: MediaButtonProps) {
  const isActive = useMediaState('pictureInPicture');
  return <Tooltip.Root data-sentry-element="unknown" data-sentry-component="PIP" data-sentry-source-file="buttons.tsx">
            <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                <PIPButton className="vds-button" data-sentry-element="PIPButton" data-sentry-source-file="buttons.tsx">
                    {isActive ? <PictureInPictureExitIcon /> : <PictureInPictureIcon />}
                </PIPButton>
            </Tooltip.Trigger>
            <Tooltip.Content className="vds-tooltip-content" placement={tooltipPlacement} data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                {isActive ? 'Exit PIP' : 'Enter PIP'}
            </Tooltip.Content>
        </Tooltip.Root>;
}
export function Fullscreen({
  tooltipPlacement
}: MediaButtonProps) {
  const isActive = useMediaState('fullscreen');
  return <Tooltip.Root data-sentry-element="unknown" data-sentry-component="Fullscreen" data-sentry-source-file="buttons.tsx">
            <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                <FullscreenButton className="vds-button" data-sentry-element="FullscreenButton" data-sentry-source-file="buttons.tsx">
                    {isActive ? <BraivExitFullScreenIcon /> : <BraivEnterFullScreenIcon />}
                </FullscreenButton>
            </Tooltip.Trigger>
            <Tooltip.Content className="vds-tooltip-content" placement={tooltipPlacement} data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                {isActive ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            </Tooltip.Content>
        </Tooltip.Root>;
}
export interface SeekButtonProps extends MediaButtonProps {
  seconds: number;
}
export function Seek({
  seconds,
  tooltipPlacement
}: SeekButtonProps) {
  const isBackward = seconds < 0;
  return <Tooltip.Root data-sentry-element="unknown" data-sentry-component="Seek" data-sentry-source-file="buttons.tsx">
            <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                <SeekButton className="vds-button" seconds={seconds} data-sentry-element="SeekButton" data-sentry-source-file="buttons.tsx">
                    {isBackward ? <SeekBackward10Icon /> : <SeekForward10Icon />}
                </SeekButton>
            </Tooltip.Trigger>
            <Tooltip.Content className="vds-tooltip-content" placement={tooltipPlacement} data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                {isBackward ? 'Seek Backward' : 'Seek Forward'}
            </Tooltip.Content>
        </Tooltip.Root>;
}
export function AirPlay({
  tooltipPlacement
}: MediaButtonProps) {
  const airPlayText = 'AirPlay',
    $state = useMediaState('remotePlaybackState'),
    stateText = $state === 'connecting' ? 'Connecting' : $state === 'connected' ? 'Connected' : 'Disconnected',
    label = `${airPlayText} ${stateText}`,
    Icon = ($state === 'connecting' ? PlayerIcons?.AirPlayButton?.Connecting : $state === 'connected' ? PlayerIcons?.AirPlayButton?.Connected : null) ?? (PlayerIcons?.AirPlayButton?.Default || AirPlayIcon);
  return <Tooltip.Root data-sentry-element="unknown" data-sentry-component="AirPlay" data-sentry-source-file="buttons.tsx">
            <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                <AirPlayButton className="vds-airplay-button vds-button" aria-label={label} data-sentry-element="AirPlayButton" data-sentry-source-file="buttons.tsx">
                    <Icon className="vds-icon" data-sentry-element="Icon" data-sentry-source-file="buttons.tsx" />
                </AirPlayButton>
            </Tooltip.Trigger>
            <Tooltip.Content className="vds-tooltip-content" placement={tooltipPlacement} data-sentry-element="unknown" data-sentry-source-file="buttons.tsx">
                airPlayText
            </Tooltip.Content>
        </Tooltip.Root>;
}
export function GoogleCast({
  tooltipPlacement
}: MediaButtonProps) {
  const googleCastText = 'Google Cast',
    $state = useMediaState('remotePlaybackState'),
    stateText = $state === 'connecting' ? 'Connecting' : $state === 'connected' ? 'Connected' : 'Disconnected',
    label = `${googleCastText} ${stateText}`,
    Icon = ($state === 'connecting' ? defaultLayoutIcons.GoogleCastButton.Connecting : $state === 'connected' ? defaultLayoutIcons.GoogleCastButton.Connected : null) ?? defaultLayoutIcons.GoogleCastButton.Default;
  return <DefaultTooltip content={googleCastText} placement={tooltipPlacement} data-sentry-element="DefaultTooltip" data-sentry-component="GoogleCast" data-sentry-source-file="buttons.tsx">
            <GoogleCastButton className="vds-google-cast-button vds-button" aria-label={label} data-sentry-element="GoogleCastButton" data-sentry-source-file="buttons.tsx">
                <Icon className="vds-icon" data-sentry-element="Icon" data-sentry-source-file="buttons.tsx" />
            </GoogleCastButton>
        </DefaultTooltip>;
}